import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import LoginAnimation from "../../components/LoginAnimation";
import { useFormik } from "formik";
import * as Yup from "yup";
import AuthService from "../../services/auth";
import { toastError, toastSuccess } from "../../components/ToastNotification";
import { useDispatch } from "react-redux";
import { setUser } from "../../store/slices/userSlice";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import LogoIcon from "../../assets/images/logo/logo-icon.jpg";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isShow, setIsShow] = useState(false);
  const [isSubmiting, setSubmiting] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("admin_lang") || "EN");

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);

  const handleLangToggle = (selected) => {
    setLang(selected);
    localStorage.setItem("admin_lang", selected);
  };

  const t = {
    heading:       lang === "EN" ? "Log in"                  : "Iniciar sesión",
    emailLabel:    lang === "EN" ? "Email address"           : "Correo electrónico",
    passwordLabel: lang === "EN" ? "Password"                : "Contraseña",
    submitBtn:     lang === "EN" ? "Log in"                  : "Ingresar",
    orDivider:     lang === "EN" ? "OR"                      : "O",
    footerNote:    lang === "EN" ? "Admin Panel Access Only" : "Solo acceso al panel de administración",
    emailErr:      lang === "EN" ? "Email is required"       : "Se requiere correo electrónico",
    passErr:       lang === "EN" ? "Password is required"    : "Se requiere contraseña",
  };

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().required(t.emailErr),
      password: Yup.string().required(t.passErr),
    }),
    onSubmit: async (values) => {
      const payload = {
        email: values.email.toLowerCase(),
        password: values.password,
      };
      try {
        setSubmiting(true);
        const response = await AuthService?.login(payload);
        if (response) {
          setIsShow(true);
          setTimeout(() => {
            setIsShow(false);
            toastSuccess("iniciar sesión exitosamente");
            localStorage.setItem("linko_admin", response?.data?.token);
            localStorage.setItem("linko_admin_details", JSON.stringify(response?.data?.data));
            dispatch(setUser(response?.data?.data));
            navigate("/user-admin");
          }, 1000);
        }
      } catch (error) {
        toastError(error?.response?.data?.message);
      } finally {
        setSubmiting(false);
      }
    },
  });

  return (
    <>
      {isShow ? (
        <LoginAnimation />
      ) : (
        <div
          className="min-h-screen flex flex-col items-center justify-center px-4"
          style={{
            background: "linear-gradient(157.06deg, #1A0E3E 46.27%, #904DB0 94.25%)",
          }}
        >
          {/* ENG/ESP toggle — top right */}
          {/* <div className="absolute top-4 right-4">
            <div className="flex items-center bg-white/10 rounded-full p-0.5">
              <button
                onClick={() => handleLangToggle("EN")}
                className={`px-4 py-1.5 rounded-full font-roboto font-semibold text-xs transition-all ${
                  lang === "EN" ? "bg-white/20 text-white shadow-sm" : "text-white/50 hover:text-white"
                }`}
              >
                ENG
              </button>
              <button
                onClick={() => handleLangToggle("ES")}
                className={`px-4 py-1.5 rounded-full font-roboto font-semibold text-xs transition-all ${
                  lang === "ES" ? "bg-white/20 text-white shadow-sm" : "text-white/50 hover:text-white"
                }`}
              >
                ESP
              </button>
            </div>
          </div> */}

          {/* LINKO logo above card */}
          <div className="flex flex-col items-center mb-6">
            <div className="h-16 w-16 rounded-full overflow-hidden border-2 border-white/20 mb-3">
              <img src={LogoIcon} alt="Linko" className="h-full w-full object-cover" />
            </div>
            <h1 className="font-roboto font-bold text-white text-xl tracking-widest uppercase">LINKO</h1>
          </div>

          {/* Login card */}
          <div
            className="w-full max-w-sm rounded-2xl p-8"
            style={{ background: "rgba(60, 30, 90, 0.85)" }}
          >
            <h2 className="font-roboto font-bold text-white text-2xl mb-6">
              {t.heading}
            </h2>

            <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
              {/* Email */}
              <div>
                <input
                  type="email"
                  name="email"
                  autoComplete="off"
                  placeholder={t.emailLabel}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="w-full rounded-xl px-4 py-3 font-roboto text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="text-red-300 text-xs mt-1">{formik.errors.email}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="relative">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    name="password"
                    autoComplete="off"
                    placeholder={t.passwordLabel}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="w-full rounded-xl px-4 pr-10 py-3 font-roboto text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {passwordVisible ? <FaEye className="h-4 w-4" /> : <FaEyeSlash className="h-4 w-4" />}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="text-red-300 text-xs mt-1">{formik.errors.password}</p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="w-full py-3 rounded-full font-roboto font-bold text-sm text-white transition-all mt-1"
                style={{ background: "linear-gradient(90deg, #7B4FA6 0%, #9B59CC 100%)" }}
              >
                {isSubmiting ? (
                  <div className="loaders flex justify-center items-center mx-auto" />
                ) : (
                  t.submitBtn
                )}
              </button>
            </form>

            {/* Divider */}
            {/* <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-white/10" />
              <span className="font-roboto text-xs text-white/40 uppercase tracking-widest">{t.orDivider}</span>
              <div className="flex-1 h-px bg-white/10" />
            </div> */}

    
          </div>
        </div>
      )}
    </>
  );
};

export default Login;
